import { supabase } from '../lib/supabase';

/**
 * Portfolio Service for calculating user portfolio balance from transactions
 */
class PortfolioService {
  /**
   * Calculate portfolio balance from completed transactions
   */
  async getPortfolioBalance(userId) {
    try {
      // Fetch all completed transactions for the user
      const { data: transactions, error } = await supabase
        ?.from('transactions')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('status', 'completed')
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      // If no transactions, return zero balance
      if (!transactions || transactions?.length === 0) {
        return {
          data: {
            totalValue: 0,
            usdBalance: 0,
            cryptoBalance: 0,
            change24h: 0,
            changePercent: 0
          },
          error: null
        };
      }

      // Calculate balances
      let usdBalance = 0;
      let cryptoValue = 0;

      transactions?.forEach(tx => {
        const amount = parseFloat(tx?.usd_amount || 0);
        
        switch (tx?.type) {
          case 'deposit':
            usdBalance += amount;
            break;
          case 'withdrawal':
            usdBalance -= amount;
            break;
          case 'buy':
            // When buying crypto, USD decreases, crypto increases
            usdBalance -= amount;
            cryptoValue += amount;
            break;
          case 'sell':
            // When selling crypto, USD increases, crypto decreases
            usdBalance += amount;
            cryptoValue -= amount;
            break;
          case 'transfer':
            // Transfer doesn't change total balance
            break;
          default:
            break;
        }
      });

      const totalValue = usdBalance + cryptoValue;

      // Calculate 24h change (simplified - comparing to yesterday's balance)
      const yesterday = new Date();
      yesterday?.setDate(yesterday?.getDate() - 1);
      
      const { data: yesterdayTransactions } = await supabase
        ?.from('transactions')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('status', 'completed')
        ?.lte('created_at', yesterday?.toISOString());

      let yesterdayBalance = 0;
      yesterdayTransactions?.forEach(tx => {
        const amount = parseFloat(tx?.usd_amount || 0);
        if (tx?.type === 'deposit' || tx?.type === 'sell') {
          yesterdayBalance += amount;
        } else if (tx?.type === 'withdrawal' || tx?.type === 'buy') {
          yesterdayBalance -= amount;
        }
      });

      const change24h = totalValue - yesterdayBalance;
      const changePercent = yesterdayBalance > 0 ? (change24h / yesterdayBalance) * 100 : 0;

      return {
        data: {
          totalValue: Math.max(0, totalValue),
          usdBalance: Math.max(0, usdBalance),
          cryptoBalance: Math.max(0, cryptoValue),
          change24h: change24h,
          changePercent: changePercent
        },
        error: null
      };
    } catch (error) {
      console.error('Failed to calculate portfolio balance:', error);
      return {
        data: {
          totalValue: 0,
          usdBalance: 0,
          cryptoBalance: 0,
          change24h: 0,
          changePercent: 0
        },
        error: error?.message
      };
    }
  }

  /**
   * Get asset allocation breakdown
   */
  async getAssetAllocation(userId) {
    try {
      const { data: transactions, error } = await supabase
        ?.from('transactions')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('status', 'completed')
        ?.not('crypto_symbol', 'is', null);

      if (error) throw error;

      if (!transactions || transactions?.length === 0) {
        return { data: [], error: null };
      }

      // Group by crypto symbol and calculate holdings
      const holdings = {};
      
      transactions?.forEach(tx => {
        const symbol = tx?.crypto_symbol;
        if (!symbol) return;

        if (!holdings?.[symbol]) {
          holdings[symbol] = {
            symbol: symbol,
            amount: 0,
            value: 0
          };
        }

        const cryptoAmount = parseFloat(tx?.crypto_amount || 0);
        const usdAmount = parseFloat(tx?.usd_amount || 0);

        if (tx?.type === 'buy') {
          holdings[symbol].amount += cryptoAmount;
          holdings[symbol].value += usdAmount;
        } else if (tx?.type === 'sell') {
          holdings[symbol].amount -= cryptoAmount;
          holdings[symbol].value -= usdAmount;
        }
      });

      // Convert to array and calculate percentages
      const portfolio = await this.getPortfolioBalance(userId);
      const totalValue = portfolio?.data?.totalValue || 0;

      const allocation = Object.keys(holdings)?.filter(symbol => holdings?.[symbol]?.amount > 0)?.map(symbol => ({
          name: symbol,
          value: Math.max(0, holdings?.[symbol]?.value),
          percentage: totalValue > 0 ? (holdings?.[symbol]?.value / totalValue) * 100 : 0
        }));

      // Add USD cash if there's a balance
      if (portfolio?.data?.usdBalance > 0) {
        allocation?.push({
          name: 'USD Cash',
          value: portfolio?.data?.usdBalance,
          percentage: totalValue > 0 ? (portfolio?.data?.usdBalance / totalValue) * 100 : 0
        });
      }

      return { data: allocation, error: null };
    } catch (error) {
      console.error('Failed to get asset allocation:', error);
      return { data: [], error: error?.message };
    }
  }
}

const portfolioService = new PortfolioService();
export default portfolioService;